/**
 * Generate a device fingerprint for tracking
 * This helps detect credential sharing by identifying unique devices
 */
export const generateDeviceFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    
    const fingerprint = {
      screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
      maxTouchPoints: navigator.maxTouchPoints || 0,
      canvas: canvas.toDataURL(),
      webgl: getWebGLFingerprint(),
      fonts: getFontFingerprint()
    };
    
    // Create a hash from the fingerprint
    const fingerprintString = JSON.stringify(fingerprint);
    return hashString(fingerprintString);
  } catch (error) {
    console.error('Error generating device fingerprint:', error);
    // Fallback to simple fingerprint
    return hashString(
      navigator.userAgent + 
      window.screen.width + 
      window.screen.height + 
      navigator.language + 
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  }
};

/**
 * Get WebGL fingerprint
 */
const getWebGLFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return 'no-webgl';
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      return {
        vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
        renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      };
    }
    return 'no-debug-info';
  } catch (e) {
    return 'error';
  }
};

/**
 * Get font fingerprint (simplified)
 */
const getFontFingerprint = () => {
  const baseFonts = ['monospace', 'sans-serif', 'serif'];
  const testString = 'mmmmmmmmmmlli';
  const testSize = '72px';
  const h = document.getElementsByTagName('body')[0];
  const s = document.createElement('span');
  s.style.fontSize = testSize;
  s.innerHTML = testString;
  const defaultWidth = {};
  const defaultHeight = {};
  
  for (let i = 0; i < baseFonts.length; i++) {
    s.style.fontFamily = baseFonts[i];
    h.appendChild(s);
    defaultWidth[baseFonts[i]] = s.offsetWidth;
    defaultHeight[baseFonts[i]] = s.offsetHeight;
    h.removeChild(s);
  }
  
  return {
    defaultWidth,
    defaultHeight
  };
};

/**
 * Simple hash function
 */
const hashString = (str) => {
  let hash = 0;
  if (str.length === 0) return hash.toString();
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Get device fingerprint and store it
 */
export const getStoredDeviceFingerprint = () => {
  const stored = localStorage.getItem('deviceFingerprint');
  if (stored) {
    return stored;
  }
  
  const fingerprint = generateDeviceFingerprint();
  localStorage.setItem('deviceFingerprint', fingerprint);
  return fingerprint;
};

